import type { Format, Omit } from "ts-vista";

import type { CodegenResult, SourceMap } from "#/ast/codegen";
import type { ParseResult } from "#/ast/parse";
import type { BundlerContext, CompilerContext } from "#/contexts/compiler";
import type {
    BundleResult,
    DynamicBundleOptions,
    PresetBundleOptions,
    UserBundleOptions,
} from "#/modules/bundler";
import type { PreprocessResult } from "#/modules/preprocessor";
import type { ProcessResult } from "#/modules/processor";

import { codegen } from "#/ast/codegen";
import { parse } from "#/ast/parse";
import { createCompilerContext } from "#/contexts/compiler";
import { bundle } from "#/modules/bundler";
import { collect } from "#/modules/collector";
import { preprocess } from "#/modules/preprocessor";
import { process } from "#/modules/processor";

type PresetCompileOptions = Format<
    Omit<PresetBundleOptions, "context"> & {
        bundler: BundlerContext;
    }
>;

type UserCompileOptions = UserBundleOptions;

type DynamicCompileOptions = DynamicBundleOptions;

/**
 * The options for the `compile` function.
 */
type CompileOptions = Format<
    PresetCompileOptions & UserCompileOptions & DynamicCompileOptions
>;

/**
 * The result of the `compile` function.
 */
type CompileResult = {
    /**
     * The transpiled code.
     */
    code: string;
    /**
     * The CSS of the transpiled code.
     */
    css: string;
    /**
     * The CSS of the transpiled code that separated by elements.
     */
    cssList: string[];
    /**
     * The source map of the transpiled code.
     */
    map: SourceMap;
};

const compile = async (
    options: CompileOptions,
): Promise<CompileResult | undefined> => {
    const parsed: ParseResult = parse({
        file: options.file,
        code: options.code,
    });

    const context: CompilerContext = createCompilerContext({
        bundler: options.bundler,
        file: options.file,
        program: parsed.program,
    });

    const { isImported, namespaces, specifiers } = collect({
        context,
        program: parsed.program,
        packageName: options.packageName,
        includedFunctions: options.includedFunctions,
    });

    if (!isImported) return void 0;

    const preprocessed: PreprocessResult = preprocess({
        context,
        program: parsed.program,
        namespaces,
        includedFunctions: options.includedFunctions,
        specifiers,
    });

    const codegenPreprocessed: CodegenResult = codegen({
        file: options.file,
        code: options.code,
        program: preprocessed.program,
    });

    const bundled: BundleResult = await bundle({
        // preset
        context,
        packageName: options.packageName,
        includedFunctions: options.includedFunctions,
        // user
        cwd: options.cwd,
        include: options.include,
        exclude: options.exclude,
        tsconfigPath: options.tsconfigPath,
        // dynamic
        file: options.file,
        code: codegenPreprocessed.code,
    });

    const parsedbundle: ParseResult = parse({
        file: options.file,
        code: bundled.code,
    });

    const processed: ProcessResult = process({
        context,
        program: preprocessed.program,
        programRef: parsedbundle.program,
    });

    const codegenResult: CodegenResult = codegen({
        file: options.file,
        code: options.code,
        program: processed.program,
    });

    return {
        code: codegenResult.code,
        css: processed.css,
        cssList: processed.cssList,
        map: codegenResult.map,
    };
};

export type {
    CompileOptions,
    CompileResult,
    DynamicCompileOptions,
    PresetCompileOptions,
    SourceMap,
    UserCompileOptions,
};
export { compile };
